import {
  type Board,
  type CouponBoardIdentifiers,
  couponBoardValidator,
  type CouponId,
  type CouponIndex,
  type OutcomeGroup,
  type QuickPick,
  type ReductionDistributionOutcome,
  toOutcome,
  toReductionDistributionOutcome,
} from '../../../schema/coupon/entry.js';
import type { EventNumber } from '../../../schema/sport/entry.js';
import { typedObjectEntries } from '../../../schema/utils/entry.js';
import { BadRequestError } from '../error/BadRequestError.js';
import type { IDefinition } from './DefinitionEntity.js';

type PartialCouponBoard = ReturnType<typeof couponBoardValidator.parse>;

type EventGroupOutcomes = {
  group: number;
  event: number;
  outcomes: number[];
};

type ValidatedEventGroupOutcomes = NonNullable<ReturnType<typeof couponBoardValidator.parse>['outcomes']>['values'];

type ValidatedQuickPickSize = ReturnType<typeof couponBoardValidator.parse>['quickPickSize'];

export interface ICouponBoardEntity {
  get couponId(): CouponId;
  get couponIndex(): CouponIndex;
  get boardIndex(): number;
  get model(): Board;

  get outcomes(): Board['outcomes'];
  get mOutcomes(): Board['mOutcomes'];
  get uOutcomes(): Board['uOutcomes'];
  get quickPickVariant(): Board['quickPickVariant'];
  get quickPickSize(): Board['quickPickSize'];

  get isComplete(): boolean;

  calculateUpdatedUserOutcomes(
    incomingOutcomes: OutcomeGroup,
    { eventNumber, group }: { eventNumber: EventNumber; group: number }
  ): OutcomeGroup;

  calculateQuickPickVariant(board: Board): QuickPick | undefined;

  calculateOutcomeDistribution(outcomes: OutcomeGroup): ReductionDistributionOutcome[];

  validateOutcomes(outcomes: number[][][]): Board['outcomes'];

  validateEventGroupOutcomes(eventGroupOutcomes: EventGroupOutcomes[]): ValidatedEventGroupOutcomes;

  validateQuickPickSize(quickPickSize: number, isSingleRow?: boolean): ValidatedQuickPickSize;

  validate(payload: unknown, isSingleRow?: boolean): PartialCouponBoard;
}

function isOutcomesEqual(board: Board): boolean {
  return board.outcomes.every((betEvent, eventIndex) =>
    betEvent.every((outcomeGroup, groupIndex) => {
      const userGroup = board.userOutcomes[eventIndex]?.[groupIndex];
      return outcomeGroup.length === userGroup?.length;
    })
  );
}

export class CouponBoardEntity implements ICouponBoardEntity {
  private readonly _model: Board;
  private readonly _couponBoardIdentifiers: CouponBoardIdentifiers;
  private readonly _definition: IDefinition;

  constructor(board: Board, couponBoardIdentifiers: CouponBoardIdentifiers, definition: IDefinition) {
    this._model = board;
    this._couponBoardIdentifiers = couponBoardIdentifiers;
    this._definition = definition;
  }

  get couponId(): CouponId {
    return this._couponBoardIdentifiers.couponId;
  }

  get couponIndex(): CouponIndex {
    return this._couponBoardIdentifiers.couponIndex;
  }

  get boardIndex(): number {
    return this._couponBoardIdentifiers.boardIndex;
  }

  get model(): Board {
    return this._model;
  }

  get outcomes(): Board['outcomes'] {
    return this.model.outcomes;
  }

  get mOutcomes(): Board['mOutcomes'] {
    return this.model.mOutcomes;
  }

  get uOutcomes(): Board['uOutcomes'] {
    return this.model.uOutcomes;
  }

  get quickPickVariant(): Board['quickPickVariant'] {
    return this.model.quickPickVariant;
  }

  get quickPickSize(): Board['quickPickSize'] {
    return this.model.quickPickSize;
  }

  get isComplete(): boolean {
    const isEveryEventSelected =
      this.model.outcomes.filter(
        (eventSelections) =>
          eventSelections.filter((eventSelection) => eventSelection.length).length === eventSelections.length
      ).length === this.model.outcomes.length;

    return isEveryEventSelected;
  }

  calculateUpdatedUserOutcomes(
    incomingOutcomes: OutcomeGroup,
    { eventNumber, group }: { eventNumber: EventNumber; group: number }
  ): OutcomeGroup {
    const boardModel = this.model;
    const userOutcomes = boardModel.userOutcomes[eventNumber - 1]?.[group] ?? [];
    const currentOutcomes = boardModel.outcomes[eventNumber - 1]?.[group] ?? [];

    const nextOutcomes = incomingOutcomes.filter((value) => !currentOutcomes.includes(value));
    const removedUserOutcomes = userOutcomes.filter((value) => !incomingOutcomes.includes(value));
    const nextUserOutcomes = nextOutcomes.filter((value) => !userOutcomes.includes(value));
    const userOutcomesToKeep = new Set(userOutcomes.filter((value) => incomingOutcomes.includes(value)));

    const hasNewUserOutcomes = nextUserOutcomes.length > 0;
    const hasRemovedUserOutcomes = removedUserOutcomes.length > 0;

    if (hasNewUserOutcomes) {
      return [...userOutcomes, ...nextUserOutcomes].filter((value) => !removedUserOutcomes.includes(value));
    }

    if (hasRemovedUserOutcomes) {
      return [...userOutcomesToKeep];
    }

    return [...userOutcomes];
  }

  calculateQuickPickVariant(board: Board): QuickPick | undefined {
    const hasUserOutcomes = board.userOutcomes.some((event) => event.some((group) => group.length > 0));
    const hasOutcomes = board.outcomes.some((event) => event.some((group) => group.length > 0));
    const isUserOutcomeEqualToBoard = isOutcomesEqual(board);

    if (board.quickPickVariant && !isUserOutcomeEqualToBoard) {
      return 'MODIFIEDQP';
    }

    if (!hasUserOutcomes && hasOutcomes) {
      return 'WEIGHTEDQP';
    }

    if (hasUserOutcomes && hasOutcomes && !isUserOutcomeEqualToBoard) {
      return 'PARTIALQP';
    }

    return undefined;
  }

  calculateOutcomeDistribution(outcomes: OutcomeGroup): ReductionDistributionOutcome[] {
    const ratio = toReductionDistributionOutcome(outcomes.length / this._definition.outcomeCount);

    return Array.from<ReductionDistributionOutcome>({ length: this._definition.outcomeCount })
      .fill(toReductionDistributionOutcome(0))
      .map((value, outcomeIndex) => (outcomes.includes(toOutcome(outcomeIndex)) ? ratio : value));
  }

  /**
   * Validates a complete coupon board.
   *
   * @deprecated use {this.validateEventGroupOutcomes} instead
   * @param boardOutcomes All outcome matrix to validate
   * @returns Validated and type casted entity
   * @throws {BadRequestError} Validation error if not citera is matched
   */
  validateOutcomes(boardOutcomes: number[][][]): Board['outcomes'] {
    const NUMBER_OF_EVENTS = this.model.outcomes.reduce((sum) => sum + 1, 0);
    const MIN_OUTCOME_VALUE = 0;
    const MAX_OUTCOME_VALUE = this._definition.outcomeCount - 1;

    if (boardOutcomes.length !== 8) {
      throw new BadRequestError(
        `Incorrect outcome length, expected: ${NUMBER_OF_EVENTS}, received: ${boardOutcomes.length}`
      );
    }

    for (const [outcomesRow, outcomeGroups] of Object.entries(boardOutcomes)) {
      if (outcomeGroups.length !== this._definition.outcomeGroupCount) {
        throw new BadRequestError(
          `Invalid outcome group length, expected ${this._definition.outcomeGroupCount}, received: ${outcomeGroups.length} at row: ${outcomesRow}`
        );
      }

      for (const [outcomesGroupPos, outcomes] of Object.entries(outcomeGroups)) {
        if (outcomes.length > this._definition.outcomeCount) {
          throw new BadRequestError(
            `Invalid outcomes length, expected: ${this._definition.outcomeCount}, received: ${outcomes.length} at row: ${outcomesRow}, group: ${outcomesGroupPos}`
          );
        }

        for (const [outcomePos, outcome] of Object.entries(outcomes)) {
          // eslint-disable-next-line max-depth
          if (outcome < MIN_OUTCOME_VALUE || outcome > MAX_OUTCOME_VALUE) {
            throw new BadRequestError(
              `Invalid outcome value, min: ${MIN_OUTCOME_VALUE}, max: ${MAX_OUTCOME_VALUE}, received: ${outcome} at row: ${outcomesRow}, group: ${outcomesGroupPos}, pos: ${outcomePos}`
            );
          }
        }
      }
    }

    return boardOutcomes as Board['outcomes'];
  }

  validateEventGroupOutcomes(eventGroupOutcomes: EventGroupOutcomes[]): ValidatedEventGroupOutcomes {
    const MIN_OUTCOME_VALUE = 0;
    const MAX_OUTCOME_VALUE = this._definition.outcomeCount - 1;
    const MIN_GROUP_VALUE = 0;
    const MAX_GROUP_VALUE = this._definition.outcomeGroupCount - 1;
    const MIN_NUMBER_OF_EVENTS = 1;
    // The best would be to have the validate methods "static" so an instance isn't needed, but it doesn't works with interfaces.
    // In the future when coupon is in a DB this might maybe be static methods, but not we will always have a reference.
    const MAX_NUMBER_OF_EVENTS = this.model.outcomes.reduce((sum) => sum + 1, 0);

    for (const { event, group, outcomes } of eventGroupOutcomes) {
      if (event < MIN_NUMBER_OF_EVENTS || event > MAX_NUMBER_OF_EVENTS) {
        throw new BadRequestError(
          `Invalid event, min: ${MIN_NUMBER_OF_EVENTS}, max: ${MAX_NUMBER_OF_EVENTS}, received: ${event}`
        );
      }

      if (group < MIN_GROUP_VALUE || group > MAX_GROUP_VALUE) {
        throw new BadRequestError(
          `Invalid group, min: ${MIN_GROUP_VALUE}, max: ${MAX_GROUP_VALUE}, received: ${group}`
        );
      }

      if (outcomes.some((outcome) => outcome < MIN_OUTCOME_VALUE || outcome > MAX_OUTCOME_VALUE)) {
        throw new BadRequestError(
          `Invalid outcomes, min: ${MIN_OUTCOME_VALUE}, max: ${MAX_OUTCOME_VALUE}, received: ${outcomes.join(', ')}`
        );
      }
    }

    return eventGroupOutcomes as ValidatedEventGroupOutcomes;
  }

  validateQuickPickSize(quickPickSize: number, isSingleRow?: boolean): ValidatedQuickPickSize {
    const { quickPickMsys, quickPickSrow } = this._definition.model;
    const permittedQuickPickValues = isSingleRow ? quickPickSrow : quickPickMsys;
    const quickPickValuesStr = permittedQuickPickValues?.join(', ');

    if (!permittedQuickPickValues?.includes(quickPickSize)) {
      throw new BadRequestError(
        `Invalid quick pick size, expected one of ${quickPickValuesStr}, received: ${quickPickSize}`
      );
    }

    return quickPickSize;
  }

  validate(payload: unknown, isSingleRow?: boolean): PartialCouponBoard {
    const parsedEntity = couponBoardValidator.parse(payload);
    const updateEntity: PartialCouponBoard = {};

    for (const [field, value] of typedObjectEntries(parsedEntity)) {
      if (!value) {
        continue;
      }

      switch (field) {
        case 'outcomes': {
          updateEntity[field] = {
            values: this.validateEventGroupOutcomes(value.values),
            intention: value.intention,
          };
          continue;
        }
        case 'quickPickVariant': {
          updateEntity[field] = value;
          continue;
        }
        case 'quickPickSize': {
          updateEntity[field] = this.validateQuickPickSize(value, isSingleRow);
          continue;
        }
        // add more validators...
        default:
          continue;
      }
    }

    return updateEntity;
  }
}
